import { useMemo, useState } from "react";
import { isMockMode } from "../../api/client";
import { formatBytes, mediaSlotLabel } from "../../api/amazonMedia";
import type { AmzMediaItem } from "../../types/amazonMedia";
import { AMZ_MEDIA_BASE } from "../../types/amazonMedia";
import type { AmzOverviewResponse } from "../../types/amazon";
import { StateBanner } from "../../components/StateBanner";
import { Badge } from "./AmzUi";
import type { DetailTab } from "./helpers";
import { useAmazonMedia } from "./useAmazonMedia";

/** 画像1枚のサムネ（3:4・はみ出さず全体表示）。モック時は外部画像を読まない（ImagesTab と同じ規約） */
export function MediaThumb({ url }: { url: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="amz-lib-thumb">
      {!failed && !isMockMode() ? <img src={url} alt="" loading="lazy" onError={() => setFailed(true)} /> : <span>{failed ? "読み込めません" : "プレビュー"}</span>}
    </div>
  );
}

interface LibraryViewProps {
  overview: AmzOverviewResponse | null;
  onOpen: (sku: string, tab: DetailTab) => void;
  showToast: (m: string) => void;
}

export function LibraryView({ overview, onOpen, showToast }: LibraryViewProps) {
  const { data, error, reload } = useAmazonMedia();
  const [sku, setSku] = useState<string>("all");
  const [showOld, setShowOld] = useState(false);

  const items = useMemo(() => (data?.items ?? []).filter((i) => showOld || i.latest || !i.sku), [data, showOld]);
  const skus = useMemo(() => {
    const m = new Map<string, number>();
    for (const i of items) m.set(i.sku || "（規約外）", (m.get(i.sku || "（規約外）") ?? 0) + 1);
    return [...m.entries()];
  }, [items]);
  const known = new Set((overview?.products ?? []).map((p) => p.sku));
  const nameOf = (s: string) => overview?.products.find((p) => p.sku === s)?.itemName ?? null;

  const groups = useMemo(() => {
    const g = new Map<string, AmzMediaItem[]>();
    for (const i of items) {
      const k = i.sku || "（規約外）";
      if (sku !== "all" && k !== sku) continue;
      g.set(k, [...(g.get(k) ?? []), i]);
    }
    return [...g.entries()];
  }, [items, sku]);

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      showToast("画像URLをコピーしました。");
    } catch {
      showToast(url);
    }
  };

  return (
    <div className="amz-page tight">
      <div className="amz-head">
        <div className="amz-head-text">
          <h1 className="amz-h1">画像ライブラリ</h1>
          <span className="amz-subtext">
            Amazon用に公開済みの画像（{AMZ_MEDIA_BASE.replace("https://", "").replace(/\/$/, "")}・無圧縮）
            {data && ` · 更新 ${data.generatedAt.slice(0, 16).replace("T", " ")}`}
          </span>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <label className="amz-lib-check">
            <input type="checkbox" checked={showOld} onChange={(e) => setShowOld(e.target.checked)} />
            旧版も表示
          </label>
          <button type="button" className="amz-btn sm" onClick={() => void reload()}>
            再読み込み
          </button>
        </div>
      </div>

      <div className="amz-note-bar">
        <span>
          商品の「画像」タブの「画像ライブラリから追加」で、ここにある画像を下書きに入れられます。新しい画像の公開・差し替えはリポジトリ側で行います（手順: amazon/images/README.md）。
        </span>
      </div>

      {error && <StateBanner kind="error" message={error} onRetry={() => void reload()} />}
      {!data && !error && <StateBanner kind="loading" message="画像ライブラリを読み込んでいます。" />}

      {data && (
        <>
          <div className="amz-chips">
            <button type="button" className={`amz-chip ${sku === "all" ? "on" : ""}`} onClick={() => setSku("all")}>
              すべて
              <small>{items.length}</small>
            </button>
            {skus.map(([s, n]) => (
              <button key={s} type="button" className={`amz-chip ${sku === s ? "on" : ""}`} onClick={() => setSku(s)}>
                {s}
                <small>{n}</small>
              </button>
            ))}
          </div>

          {groups.length === 0 && <div className="amz-empty">画像がありません。</div>}
          {groups.map(([s, list]) => (
            <section key={s} className="amz-lib-group">
              <div className="amz-lib-group-head">
                <div>
                  <b className="amz-mono">{s}</b>
                  {nameOf(s) && <span className="amz-lib-name">{nameOf(s)}</span>}
                </div>
                {known.has(s) && (
                  <button type="button" className="amz-btn xs" onClick={() => onOpen(s, "images")}>
                    商品の画像タブを開く
                  </button>
                )}
              </div>
              <div className="amz-lib-grid">
                {list.map((it) => (
                  <div key={it.file} className="amz-lib-card">
                    <MediaThumb url={it.url} />
                    <div className="amz-lib-meta">
                      <div className="amz-lib-row">
                        <Badge tone={it.slot === "main" ? "accent" : "neutral"} size="xs">
                          {mediaSlotLabel(it)}
                        </Badge>
                        {!it.latest && it.sku && (
                          <Badge tone="warning" size="xs">
                            旧版
                          </Badge>
                        )}
                        <span className="amz-lib-ver">v{it.version}</span>
                      </div>
                      {it.caption && <span className="amz-lib-cap">{it.caption}</span>}
                      <span className="amz-lib-file" title={it.file}>
                        {it.file}
                      </span>
                      <span className="amz-lib-size">
                        {it.width}×{it.height} · {formatBytes(it.bytes)}
                      </span>
                      <div className="amz-lib-row">
                        <button type="button" className="amz-btn xs" onClick={() => void copy(it.url)}>
                          URLをコピー
                        </button>
                        <a className="amz-btn xs" href={it.url} target="_blank" rel="noreferrer">
                          原寸で開く
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}
